import { createSlice } from "@reduxjs/toolkit";
import { ENSARAS_ADMIN_SCOPE, CLIENT_ADMIN_SCOPE, MQTT_ACCESS_SCOPE } from "./scopes";

// SECURITY: Use localStorage for non-sensitive user data
// Tokens are NOT stored in localStorage to prevent XSS attacks - the OIDC
// access token lives only in the OIDC library's in-memory session state
// (see ../auth/oidcConfig, ADR-0001), never in Redux or storage.

// Helper function to safely get from localStorage (non-sensitive data only)
const getFromStorage = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (error) {
    console.error(`Error reading ${key} from localStorage:`, error);
    return null;
  }
};

// Helper function to safely set to localStorage (non-sensitive data only)
const setToStorage = (key, value) => {
  try {
    if (value !== null && value !== undefined) {
      localStorage.setItem(key, value);
    } else {
      localStorage.removeItem(key);
    }
  } catch (error) {
    console.error(`Error writing ${key} to localStorage:`, error);
  }
};

const initialState = {
  userInfo: {
    username: getFromStorage("username"),
    role: getFromStorage("role"),
    userId: getFromStorage("userId"),
    email: getFromStorage("email"),
  },
  activeTab: getFromStorage("activeTab"),
  isAuthenticated: false,
  isLoading: false,
  isAIAgent: getFromStorage("isAIAgent") === "true",
  // Mirrors the OIDC library's Scopes (see ./OidcReduxScopeBridge and ticket
  // 07) - lets the plain (state) => boolean selectors below stay simple
  // Redux selectors rather than hooks.
  scopes: [],
  isLoggingOut: false,
};

const authUserSlice = createSlice({
  name: "authUser",
  initialState,
  reducers: {
    setActiveTab: (state, action) => {
      state.activeTab = action.payload;
      setToStorage("activeTab", action.payload);
    },

    updateRole: (state, action) => {
      state.userInfo.role = action.payload;
      setToStorage("role", action.payload);
    },

    setIsAIAgent: (state, action) => {
      state.isAIAgent = action.payload;
      setToStorage("isAIAgent", action.payload ? "true" : "false");
    },

    logout: (state) => {
      state.userInfo.userId = null;
      state.userInfo.username = null;
      state.userInfo.role = null;
      state.userInfo.email = null;
      state.activeTab = null;
      state.isAuthenticated = false;
      state.isLoading = false;
      state.isAIAgent = false;
      state.scopes = [];

      localStorage.clear();
    },

    setIsLoggingOut: (state, action) => {
      state.isLoggingOut = action.payload;
    },

    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },

    // Bridges the OIDC library's auth state into Redux (see
    // ./OidcReduxScopeBridge and ticket 07). Populates userInfo's
    // display-only fields (userId/username/email) from ID token claims -
    // never the access token itself, which stays out of Redux entirely
    // (ADR-0001).
    setOidcAuthState: (state, action) => {
      const { isAuthenticated, scopes, userId, username, email } = action.payload;
      state.isAuthenticated = isAuthenticated;
      state.scopes = scopes;
      state.userInfo.userId = userId ?? null;
      state.userInfo.username = username ?? null;
      state.userInfo.email = email ?? null;
    },
  },
});

export const {
  setActiveTab,
  updateRole,
  setIsAIAgent,
  logout,
  setLoading,
  setIsLoggingOut,
  setOidcAuthState,
} = authUserSlice.actions;

// Basic Selectors
export const selectAuthUser = (state) => state.authUser;
export const selectUserInfo = (state) => state.authUser.userInfo;
export const selectActiveTab = (state) => state.authUser.activeTab;
export const selectIsAuthenticated = (state) => state.authUser.isAuthenticated;
export const selectIsLoading = (state) => state.authUser.isLoading;
export const selectIsAIAgent = (state) => state.authUser.isAIAgent;
export const selectIsLoggingOut = (state) => state.authUser.isLoggingOut;

// Authorization selectors - Scope-based (see docs/specs/0001-oidc-migration.md,
// ticket 07). Plain (state) => boolean selectors so call sites can use
// useSelector directly.
const selectScopes = (state) => state.authUser.scopes ?? [];

export const selectIsEnsarasAdmin = (state) =>
  selectScopes(state).includes(ENSARAS_ADMIN_SCOPE);

export const selectIsClientAdmin = (state) =>
  selectScopes(state).includes(CLIENT_ADMIN_SCOPE);

export const selectIsAdmin = (state) =>
  selectIsEnsarasAdmin(state) || selectIsClientAdmin(state);

// Feature permission selectors
export const selectCanExportData = (state) => selectIsAdmin(state);
export const selectCanViewAdminFeatures = (state) => selectIsAdmin(state);
export const selectCanManageUsers = (state) => selectIsAdmin(state);
export const selectCanManageTeams = (state) => selectIsAdmin(state);
export const selectCanViewAlerts = (state) => !!state.authUser.isAuthenticated;

// Complex permission selectors for specific features
export const selectCanAccessMqttFeatures = (state, teamId) => {
  // teamId is intentionally not consulted here: the hardcoded allowlist
  // this replaced was this frontend's proxy for "does this user's team have
  // MQTT access" - under Scopes, that decision is the backend's to make when
  // it issues (or withholds) the Scope, so its mere presence is the complete
  // check. No new permission granularity is added or lost, it's just
  // enforced server-side instead of client-side (see ticket 07).
  return (
    selectScopes(state).includes(MQTT_ACCESS_SCOPE) &&
    !!state.authUser.isAuthenticated
  );
};

export default authUserSlice.reducer;
